import { Component, DestroyRef, HostListener, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ActionPlanFilters, ReviewerService } from '../core/services/reviewer.service';
import { Router, RouterModule } from '@angular/router';
import { ReviewManagementDashboardComponent, ReviewManagementTileFilter } from './review-management-dashboard/review-management-dashboard.component';
import { RiskManagementDashboardComponent, RiskManagementTileFilter } from './risk-management-dashboard/risk-management-dashboard.component';
import { IssueCategoryDashboardComponent, IssueCategoryTileFilter } from './issue-category-dashboard/issue-category-dashboard.component';
import { ActionRequiredDashboardComponent } from './action-required-dashboard/action-required-dashboard.component';
import { MasterService } from '../core/services/master.service';
import { RebarAuthService } from '../core/rebarauth/rebar.auth.service';

export interface DashboardFilters {
  financialYear: string;
  quarter: string;
  month: string;
  teamMembers: string;
  client: string;
  region: string;
  country: string;
  location: string;
  reviewType: string;
}

export interface TeamMemberOption {
  label: string;
  value: string;
  role: string;
}

export interface IRegionCountryCity {
  RegionID: number;
  RegionName: string;
  RegionCountryID: number;
  CountryName: string;
  RegionCityID: number;
  CityName: string;
}

@Component({
  standalone: true,
  selector: 'app-dashboard',
  imports: [CommonModule, FormsModule, RouterModule, ActionRequiredDashboardComponent, ReviewManagementDashboardComponent, RiskManagementDashboardComponent, IssueCategoryDashboardComponent],
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.css',
})
export class DashboardComponent implements OnInit {
  private destroyRef = inject(DestroyRef);

  /** Fiscal quarter (Sep-Aug fiscal year) to the calendar months it covers, in fiscal order. */
  private readonly quarterMonths: Record<string, string[]> = {
    Q1: ['September', 'October', 'November'],
    Q2: ['December', 'January', 'February'],
    Q3: ['March', 'April', 'May'],
    Q4: ['June', 'July', 'August'],
  };

  private readonly monthAbbreviations: Record<string, string> = {
    Jan: 'January', Feb: 'February', Mar: 'March', Apr: 'April',
    May: 'May', Jun: 'June', Jul: 'July', Aug: 'August',
    Sep: 'September', Oct: 'October', Nov: 'November', Dec: 'December',
  };

  currentFinancialYear = '';
  currentQuarter = '';
  currentMonth = '';
  /** Flat Region/Country/City rows from getRegionCountryCity, used to build the cascading dropdowns. */
  regionCountryCityData: IRegionCountryCity[] = [];

 
  rawActionItems: any[] = [];
  actionItemsLoading = true;
  pendingActions: any;
  activityFeed: any;
  reviewDashboardCounts: any;
  dashboardCountsLoading = false;

  constructor(
    private reviewerService: ReviewerService,
    private masterService: MasterService,
    private authService: RebarAuthService,
    private router: Router,
  ) {
     this.setCurrentFiscalContext();
    this.applyDefaultFiscalSelection();
  }

  ngOnInit(): void {

    this.reviewerService.TestDBconnection()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {},
        error: (error) => console.error('TestDBconnection error:', error),
      });

    const enterpriseId = `${this.authService.getUserName() ?? ''}@accenture.com`;
    this.masterService.getUserActionItems(enterpriseId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: any) => {
          this.rawActionItems = Array.isArray(response) ? response
                              : Array.isArray(response?.value) ? response.value
                              : Array.isArray(response?.data)  ? response.data
                              : [];
          this.actionItemsLoading = false;
        },
        error: (err) => {
          console.error('getUserActionItems error:', err);
          this.actionItemsLoading = false;
        },
      });

    this.reviewerService.getUsersInformation('')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: any) => {
          this.teamMembersList = this.mapActiveManagersAndSupervisors(response);
        },
        error: (err) => {
          console.error('GetUsersInformation error:', err?.message);
        }
      });

    this.reviewerService.getProjectDetails()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: any[]) => {
          const list: any[] = Array.isArray(response) ? response : [];
          this.masterClientNames = Array.from(
            new Set(list.map((p: any) => p.ClientName).filter(Boolean))
          ).sort() as string[];
        },
        error: (err) => {
          console.error('getProjectDetails error:', err?.message);
        }
      });

    this.reviewerService.getPurposedYear()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: any[]) => {
          const list: any[] = Array.isArray(response) ? response : [];
          this.proposedYears = list;
          this.financialYears = list
            .map((y: any) => String(y?.DisplayName ?? '').trim())
            .filter(Boolean)
            .sort((first, second) => second.localeCompare(first, undefined, { numeric: true }));
          this.reconcileCurrentFinancialYear();
          this.loadReviewDashboardCounts();
        },
        error: (err) => {
          console.error('GetProposedYear error:', err?.message);
        }
      });

      this.masterService.getRegionCountryCity()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: any) => {
          this.regionCountryCityData = Array.isArray(response) ? response : (response?.value ?? []);
        },
        error: (err) => {
          console.error('getRegionCountryCity error:', err?.message);
        }
      });

      this.reviewerService.GetWelcomeMessage()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          // connection check — no UI action required on success
        },
        error: (error) => {
          console.error('TestDBconnection error:', error);
        }
      });
  }

  /** Builds the GetReviewDashboardCounts payload from the current filter selections and fetches the counts. */
  loadReviewDashboardCounts(): void {
    this.dashboardCountsLoading = true;
    this.masterService.getReviewDashboardCounts(this.buildDashboardFilterPayload())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: any) => { 
          this.reviewDashboardCounts = response; 
          this.dashboardCountsLoading = false;
        },
        error: (err) => { console.error('GetReviewDashboardCounts error:', err?.message); this.dashboardCountsLoading = false; },
      });
  }

  private buildDashboardFilterPayload(): {
    PropsedYearId: string | null;
    Quarter: string | null;
    ProposedMonth: string | null;
    ClientName: string | null;
    DeliveryCountry: string | null;
    LocationToBeReviewed: string | null;
    TeamMembers: string | null;
    Region: string | null;
    ReviewTypeID: string | null;
  } {
    return {
      PropsedYearId: this.allFinancialYearsSelected ? null : this.getSelectedProposedYearIds(),
      Quarter: this.allQuartersSelected ? null : (this.filters.quarter || null),
      ProposedMonth: this.allMonthsSelected ? null : (this.filters.month || null),
      ClientName: this.filters.client || null,
      DeliveryCountry: this.filters.country || null,
      LocationToBeReviewed: this.filters.location || null,
      TeamMembers: this.filters.teamMembers && this.filters.teamMembers !== 'All Teams' ? this.filters.teamMembers : null,
      Region: this.filters.region && this.filters.region !== 'All Regions' ? this.filters.region : null,
      ReviewTypeID: this.filters.reviewType && this.filters.reviewType !== 'All Reviews' ? this.filters.reviewType : null,
    };
  }

  onReviewManagementTileClick(tileFilter: ReviewManagementTileFilter): void {
    const payload = {
      ReviewID: null,
      ...this.buildDashboardFilterPayload(),
      ReviewTypeID: tileFilter.ReviewTypeID ?? null,
      ReviewStatus: tileFilter.ReviewStatus ?? null,
      OverallReportRating: tileFilter.OverallReportRating ?? null,
    };

    // Previous behavior retained for reference: tile clicks only called the counts API.
    // this.masterService.getReviewDashboardCounts(payload)
    //   .pipe(takeUntilDestroyed(this.destroyRef))
    //   .subscribe({
    //     next: (response: any) => console.log('Review Distribution API response:', response),
    //     error: (error) => console.error('Review Distribution API error:', error),
    //   });

    this.openDashboardPage('/review-management', 'pending_reviewDetailsFilters', payload);

  }

  onRiskManagementTileClick(tileFilter: RiskManagementTileFilter): void {
    const payload = {
      ...this.buildDashboardFilterPayload(),
      IssueStatus: tileFilter.IssueStatus ?? null,
      IssueRatings: tileFilter.IssueRating ?? null,
      ActionPlanStatus: tileFilter.ActionPlanStatus ?? null,
      IssueCategory: tileFilter.IssueCategory ?? null,
    };

    if(tileFilter.ActionPlanStatus != null) {
      this.onActionPlanTileClick(tileFilter);
      return;
    }

    this.openDashboardPage('/issue-management', 'pending_issueDetailsFilters', payload);
  }

  onActionPlanTileClick(tileFilter: RiskManagementTileFilter ): void {
    const payload = {
      ...this.buildDashboardFilterPayload(),
      IssueStatus: tileFilter.IssueStatus ?? null,
      IssueRatings: tileFilter.IssueRating ?? null,
      ActionPlanStatus: tileFilter.ActionPlanStatus ?? null,
      IssueCategory: tileFilter.IssueCategory ?? null,
    };
    this.openDashboardPage('/action-plan', 'pending_actionPlanFilters', payload);
  }

  private openDashboardPage(path: string, storageKeyPrefix: string, payload: object): void {
    const filterToken = typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    localStorage.setItem(`${storageKeyPrefix}:${filterToken}`, JSON.stringify(payload));

    const url = this.router.serializeUrl(this.router.createUrlTree([path], {
      queryParams: { dashboardFilterToken: filterToken },
    }));
    window.open(url, '_blank');
  }

  /** Maps the selected Financial Year DisplayName(s) to their ProposedYearId(s) for the payload (comma-separated for multiple). */
  private getSelectedProposedYearIds(): string | null {
    const selectedNames = (this.filters.financialYear ?? '')
      .split(',')
      .map(name => name.trim())
      .filter(Boolean);
    if (selectedNames.length === 0) return null;
    const ids = selectedNames
      .map(name => this.proposedYears.find(
        y => String(y?.DisplayName ?? '').trim().toLowerCase() === name.toLowerCase()
      )?.ProposedYearId)
      .filter(id => id != null);
    return ids.length ? ids.join(',') : null;
  }

  /** Re-fetches the dashboard counts whenever a filter selection changes. */
 onFilterChange(): void {
  if (this.filters.quarter) {
    const selectedQuarters = this.filters.quarter.split(',').map(q => q.trim());
    const validMonths = selectedQuarters.flatMap(q => this.quarterMonths[q] ?? []);

    if (this.filters.month && !validMonths.includes(this.filters.month)) {
      this.filters.month = '';
    }
  }

  this.loadReviewDashboardCounts();
}

  /** The single GetReviewDashboardCounts result object (the API returns it wrapped in an array). */
  get dashboardData(): any {
    return Array.isArray(this.reviewDashboardCounts) ? this.reviewDashboardCounts[0] : this.reviewDashboardCounts;
  }

  /** Distinct regions for the Region dropdown. */
  get regionOptions(): string[] {
    const names = new Set<string>();
    for (const row of this.regionCountryCityData) {
      if (row?.RegionName) names.add(row.RegionName);
    }
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }

  /** Countries belonging to the selected region (all countries when no specific region is chosen). */
  get countryOptions(): string[] {
    const hasRegion = !!this.filters.region && this.filters.region !== 'All Regions';
    const rows = hasRegion
      ? this.regionCountryCityData.filter(row => row.RegionName === this.filters.region)
      : this.regionCountryCityData;
    const names = new Set<string>();
    for (const row of rows) {
      if (row?.CountryName) names.add(row.CountryName);
    }
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }

  get locationOptions(): string[] {
    const hasRegion = !!this.filters.region && this.filters.region !== 'All Regions';
    const hasCountry = !!this.filters.country;
    const rows = this.regionCountryCityData.filter(row =>
      (!hasRegion || row.RegionName === this.filters.region) &&
      (!hasCountry || row.CountryName === this.filters.country)
    );
    const seen = new Set<string>();
    const names: string[] = [];
    for (const row of rows) {
      if (!row?.CityName) continue;
      const key = `${row.RegionName}||${row.CountryName}||${row.CityName}`;
      if (seen.has(key)) continue;
      seen.add(key);
      names.push(row.CityName);
    }
    return names.sort((a, b) => a.localeCompare(b));
  }

  /** Region changed — clear the dependent country and location selections. */
  onRegionChange(): void {
    this.filters.country = '';
    this.filters.location = '';
    this.loadReviewDashboardCounts();
  }

  /** Country changed — clear the dependent location selection. */
  onCountryChange(): void {
    this.filters.location = '';
    this.loadReviewDashboardCounts();
  }

  /** Searchable Region / Country / Location dropdowns (same behaviour as Client Name). */
  openGeoDropdown: 'region' | 'country' | 'location' | null = null;
  geoSearchTerm = '';

  toggleGeoDropdown(kind: 'region' | 'country' | 'location'): void {
    this.openGeoDropdown = this.openGeoDropdown === kind ? null : kind;
    this.geoSearchTerm = '';
  }

  filterGeo(options: string[]): string[] {
    const term = this.geoSearchTerm.trim().toLowerCase();
    return term ? options.filter(o => o.toLowerCase().includes(term)) : options;
  }

  selectRegion(value: string): void {
    this.filters.region = value;
    this.closeGeoDropdown();
    this.onRegionChange();
  }

  selectCountry(value: string): void {
    this.filters.country = value;
    this.closeGeoDropdown();
    this.onCountryChange();
  }

  selectLocation(value: string): void {
    this.filters.location = value;
    this.closeGeoDropdown();
    this.onFilterChange();
  }

  private closeGeoDropdown(): void {
    this.openGeoDropdown = null;
    this.geoSearchTerm = '';
  }

  /** Aligns the calculated current FY and default selection with the API DisplayName values. */
  private reconcileCurrentFinancialYear(): void {
    const previousCurrent = this.currentFinancialYear;
    const match = this.financialYears.find(
      fy => fy.toLowerCase() === previousCurrent.toLowerCase()
    );
    if (!match || match === previousCurrent) {
      return;
    }
    this.currentFinancialYear = match;
    this.selectedFinancialYears = this.selectedFinancialYears.map(
      fy => (fy.toLowerCase() === previousCurrent.toLowerCase() ? match : fy)
    );
    this.filters.financialYear = this.selectedFinancialYears.join(',');
  }

  masterClientNames: string[] = [];
  clientSearchTerm = '';
  clientDropdownOpen = false;

  get filteredClientNames(): string[] {
    const term = this.clientSearchTerm.trim().toLowerCase();
    if (!term) return this.masterClientNames;
    return this.masterClientNames.filter(n => n.toLowerCase().includes(term));
  }

  toggleClientDropdown(): void {
    this.clientDropdownOpen = !this.clientDropdownOpen;
    this.clientSearchTerm = '';
  }

  selectClient(name: string): void {
    this.filters.client = name;
    this.clientDropdownOpen = false;
    this.clientSearchTerm = '';
    this.loadReviewDashboardCounts();
  }

  /** Location (standard review) managers/supervisors that are active — options for the Team Members dropdown. */
  teamMembersList: TeamMemberOption[] = [];

  isAllTeamsSelected = true;
  /** Whether the "All Teams" checkbox itself is ticked. */
  allTeamsChecked = true;
  selectedTeamMembers: string[] = [];
  teamMembersDropdownOpen = false;
  teamMembersSearchTerm = '';

  /** Normalises the GetUsersInformation (GetSupervisorInformation) response into dropdown options; the API already returns only active managers/supervisors. */
  private mapActiveManagersAndSupervisors(response: any): TeamMemberOption[] {
    const list: any[] = Array.isArray(response)
      ? response
      : (response?.value ?? response?.Users ?? response?.data ?? []);
    return list
      .map((u: any) => {
        const enterpriseId = (u?.EID ?? u?.EnterpriseId ?? u?.EnterpriseID ?? '').toString().trim();
        const email = (u?.InternetMail ?? u?.Email ?? u?.EmailId ?? u?.Mail ?? '').toString().trim();
        const value = enterpriseId || email;
        const label = enterpriseId || (u?.DisplayName ?? u?.UserName ?? u?.Name ?? u?.FullName ?? email).toString().trim();
        const role = (u?.Role_Name ?? u?.RoleName ?? '').toString().trim();
        return { label: (label || value).toString().trim(), value: (value || label).toString().trim(), role };
      })
      .filter(o => !!o.value)
      .sort((a, b) => a.label.localeCompare(b.label));
  }

  get filteredTeamMembersList(): TeamMemberOption[] {
    const term = this.teamMembersSearchTerm.trim().toLowerCase();
    if (!term) return this.teamMembersList;
    return this.teamMembersList.filter(m => m.label.toLowerCase().includes(term));
  }

  get teamMembersLabel(): string {
    if (this.allTeamsChecked) return 'All Teams';
    if (this.selectedTeamMembers.length === 0) return 'Select Team Member';
    const selectedNames = this.teamMembersList
      .filter(m => this.selectedTeamMembers.includes(m.value))
      .map(m => m.label)
      .join(', ');
    return selectedNames || 'Select Team Member';
  }

  toggleTeamMembersDropdown(): void {
    this.teamMembersDropdownOpen = !this.teamMembersDropdownOpen;
    this.teamMembersSearchTerm = '';
  }

  selectAllTeams(): void {
    // Toggle: unchecking "All Teams" clears every member's checkbox too.
    this.allTeamsChecked = !this.allTeamsChecked;
    this.isAllTeamsSelected = this.allTeamsChecked;
    this.selectedTeamMembers = [];
    this.filters.teamMembers = 'All Teams';
    this.loadReviewDashboardCounts();
  }

  toggleTeamMember(value: string): void {
    if (this.allTeamsChecked) {
      // Deselecting one member out of "All Teams" — keep everyone else checked.
      this.selectedTeamMembers = this.teamMembersList.map(m => m.value).filter(v => v !== value);
      this.allTeamsChecked = false;
      this.isAllTeamsSelected = false;
    } else {
      const index = this.selectedTeamMembers.indexOf(value);
      if (index > -1) {
        this.selectedTeamMembers.splice(index, 1);
      } else {
        this.selectedTeamMembers.push(value);
      }
      this.isAllTeamsSelected = this.selectedTeamMembers.length === 0;
    }
    this.filters.teamMembers = this.isAllTeamsSelected ? 'All Teams' : this.selectedTeamMembers.join(',');
    this.loadReviewDashboardCounts();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (this.teamMembersDropdownOpen && !target.closest('.team-members-select')) {
      this.teamMembersDropdownOpen = false;
    }
    if (this.clientDropdownOpen && !target.closest('.client-name-select:not(.geo-select)')) {
      this.clientDropdownOpen = false;
    }
    if (this.openGeoDropdown && !target.closest('.geo-select')) {
      this.closeGeoDropdown();
    }
    if (this.financialYearDropdownOpen && !target.closest('.financial-year-select')) {
      this.financialYearDropdownOpen = false;
    }
    if (this.quarterDropdownOpen && !target.closest('.quarter-select')) {
      this.quarterDropdownOpen = false;
    }
    if (this.monthDropdownOpen && !target.closest('.month-select')) {
      this.monthDropdownOpen = false;
    }
    if (this.reviewTypeDropdownOpen && !target.closest('.review-type-select')) {
      this.reviewTypeDropdownOpen = false;
    }
  }

  /** Derives the current Fiscal Year (Jan-Dec calendar year), Quarter and Month labels from today's date. */
  private setCurrentFiscalContext(): void {
    const today = new Date();
    const month = today.getMonth(); // 0 = January ... 11 = December
    this.currentFinancialYear = 'FY' + String(today.getFullYear()).slice(-2);
    this.currentQuarter = Object.keys(this.quarterMonths)
      .find(q => this.quarterMonths[q].includes(this.months[month])) ?? 'Q1';
    this.currentMonth = this.months[month];
  }

  private applyDefaultFiscalSelection(): void {
    this.selectedFinancialYears = [this.currentFinancialYear];
    this.selectedQuarters = [this.currentQuarter];
    this.selectedMonths = [this.currentMonth];
    this.selectedReviewTypeIds = this.reviewType.map(type => type.id);
    this.filters.financialYear = this.currentFinancialYear;
    this.filters.quarter = this.currentQuarter;
    this.filters.month = this.currentMonth;
    this.filters.reviewType = 'All Reviews';
  }

  financialYearDropdownOpen = false;
  quarterDropdownOpen = false;
  monthDropdownOpen = false;
  reviewTypeDropdownOpen = false;

  selectedFinancialYears: string[] = [];
  selectedQuarters: string[] = [];
  selectedMonths: string[] = [];
  selectedReviewTypeIds: number[] = [];

  get financialYearLabel(): string {
    if (this.allFinancialYearsSelected) return 'All Financial Years';
    return this.selectedFinancialYears.length ? this.selectedFinancialYears.join(', ') : 'Select Financial Year';
  }

  get quarterLabel(): string {
    if (this.allQuartersSelected) return 'All Quarters';
    return this.selectedQuarters.length ? this.selectedQuarters.join(', ') : 'Select Quarter';
  }

  get monthLabel(): string {
    if (this.allMonthsSelected) return 'All Months';
    return this.selectedMonths.length ? this.selectedMonths.join(', ') : 'Select Month';
  }

  get reviewTypeLabel(): string {
    if (this.allReviewTypesSelected) return 'All Types';
    const selectedNames = this.reviewType
      .filter(type => this.selectedReviewTypeIds.includes(type.id))
      .map(type => type.name);
    return selectedNames.length ? selectedNames.join(', ') : 'Select Type';
  }

  get allFinancialYearsSelected(): boolean {
    return this.financialYears.length > 0 &&
      this.financialYears.every(fy => this.selectedFinancialYears.includes(fy));
  }

  get allQuartersSelected(): boolean {
    return this.quarters.length > 0 &&
      this.quarters.every(quarter => this.selectedQuarters.includes(quarter));
  }

  get allMonthsSelected(): boolean {
    const months = this.availableMonths;
    return months.length > 0 && months.every(month => this.selectedMonths.includes(month));
  }

  get allReviewTypesSelected(): boolean {
    return this.reviewType.length > 0 &&
      this.reviewType.every(type => this.selectedReviewTypeIds.includes(type.id));
  }

  get availableMonths(): string[] {
    const selectedQuarters = (this.filters.quarter ?? '').split(',').map(q => q.trim()).filter(Boolean);
    if (selectedQuarters.length === 0) return this.months;
    const available: string[] = [];
    for (const q of selectedQuarters) {
      for (const m of this.quarterMonths[q] ?? []) {
        if (!available.includes(m)) available.push(m);
      }
    }
    return available;
  }

  toggleFinancialYearDropdown(): void {
    this.financialYearDropdownOpen = !this.financialYearDropdownOpen;
  }

  toggleQuarterDropdown(): void {
    this.quarterDropdownOpen = !this.quarterDropdownOpen;
  }

  toggleMonthDropdown(): void {
    this.monthDropdownOpen = !this.monthDropdownOpen;
  }

  toggleReviewTypeDropdown(): void {
    this.reviewTypeDropdownOpen = !this.reviewTypeDropdownOpen;
  }

  selectAllFinancialYears(): void {
    this.selectedFinancialYears = this.allFinancialYearsSelected ? [] : [...this.financialYears];
    this.onFinancialYearChange();
  }

  selectAllQuarters(): void {
    this.selectedQuarters = this.allQuartersSelected ? [] : [...this.quarters];
    this.onQuarterChange(false);
  }

  selectAllMonths(): void {
    this.selectedMonths = this.allMonthsSelected ? [] : [...this.availableMonths];
    this.onMonthChange();
  }

  selectAllReviewTypes(): void {
    this.selectedReviewTypeIds = this.allReviewTypesSelected
      ? []
      : this.reviewType.map(type => type.id);
    this.onReviewTypeChange();
  }

  toggleFinancialYear(fy: string): void {
    const index = this.selectedFinancialYears.indexOf(fy);
    if (index > -1) {
      this.selectedFinancialYears.splice(index, 1);
    } else {
      this.selectedFinancialYears.push(fy);
    }
    this.onFinancialYearChange();
  }

  toggleQuarter(q: string): void {
    const index = this.selectedQuarters.indexOf(q);
    if (index > -1) {
      this.selectedQuarters.splice(index, 1);
    } else {
      this.selectedQuarters.push(q);
    }
    this.onQuarterChange(true);
  }

  toggleMonth(m: string): void {
    const index = this.selectedMonths.indexOf(m);
    if (index > -1) {
      this.selectedMonths.splice(index, 1);
    } else {
      this.selectedMonths.push(m);
    }
    this.onMonthChange();
  }

  toggleReviewType(reviewTypeId: number): void {
    const index = this.selectedReviewTypeIds.indexOf(reviewTypeId);
    if (index > -1) {
      this.selectedReviewTypeIds.splice(index, 1);
    } else {
      this.selectedReviewTypeIds.push(reviewTypeId);
    }
    this.onReviewTypeChange();
  }

  /** Cascades a Financial Year change down to Quarter and Month, pre-selecting the current quarter/month when the current FY is (re)selected. */
  private onFinancialYearChange(): void {
    this.filters.financialYear = this.selectedFinancialYears.join(',');
    if (this.selectedFinancialYears.includes(this.currentFinancialYear)) {
      if (this.selectedQuarters.length === 0) {
        this.selectedQuarters = [this.currentQuarter];
      }
    } else {
      this.selectedQuarters = [];
      this.selectedMonths = [];
    }
    this.onQuarterChange(true);
  }

  private onQuarterChange(allowMonthDefault: boolean): void {
  this.filters.quarter = this.selectedQuarters.join(',');

  const validMonths = this.availableMonths;

  this.selectedMonths = this.selectedMonths.filter(month =>
    validMonths.includes(month)
  );

  if (
    allowMonthDefault &&
    this.selectedMonths.length === 0 &&
    this.selectedFinancialYears.includes(this.currentFinancialYear) &&
    this.selectedQuarters.includes(this.currentQuarter)
  ) {
    this.selectedMonths = [this.currentMonth];
  }

  this.filters.month = this.selectedMonths.join(',');
  this.loadReviewDashboardCounts();
}




  private onMonthChange(): void {
    this.filters.month = this.selectedMonths.join(',');
    this.loadReviewDashboardCounts();
  }

  private onReviewTypeChange(): void {
    this.filters.reviewType = this.allReviewTypesSelected
      ? 'All Reviews'
      : this.selectedReviewTypeIds.join(',');
    this.loadReviewDashboardCounts();
  }

  private extractMonthFromDate(dateStr: string): string | null {
    if (!dateStr) return null;
    const slashMatch = dateStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (slashMatch) {
      return this.months[parseInt(slashMatch[1], 10) - 1] ?? null;
    }
    const dashMatch = dateStr.match(/^\d{1,2}-([A-Za-z]{3})-\d{4}$/);
    if (dashMatch) {
      return this.monthAbbreviations[dashMatch[1]] ?? null;
    }
    return null;
  }

  /** True when the given widget date falls within the selected month(s), or when no month filter is active. */
  private matchesSelectedMonth(dateStr: string): boolean {
    if (this.selectedMonths.length === 0) return true;
    const month = this.extractMonthFromDate(dateStr);
    if (!month) return true;
    return this.selectedMonths.includes(month);
  }

  /** True when the given assignee name matches one of the selected team members (or All Teams is selected). */
  private matchesSelectedTeamMembers(assignedName: string): boolean {
    if (this.isAllTeamsSelected || this.selectedTeamMembers.length === 0) return true;
    const name = (assignedName ?? '').toString().trim().toLowerCase();
    if (!name) return false;
    const selectedLabels = this.teamMembersList
      .filter(m => this.selectedTeamMembers.includes(m.value))
      .map(m => m.label.toLowerCase());
    return selectedLabels.some(label => label.includes(name) || name.includes(label));
  }

  get filteredOverdueItems() {
    return this.overdueItems.filter(item => this.matchesSelectedTeamMembers(item.assigned) && this.matchesSelectedMonth(item.planDate));
  }

  get filteredUpcomingMilestones() {
    return this.upcomingMilestones.filter(item => this.matchesSelectedTeamMembers(item.assigned) && this.matchesSelectedMonth(item.planDate));
  }

  get filteredPendingActions() {
    return this.pendingActions.filter(item => this.matchesSelectedTeamMembers(item.assigned));
  }

  get filteredActivityFeed() {
    return this.activityFeed.filter(item => this.matchesSelectedTeamMembers(item.user) && this.matchesSelectedMonth(item.date));
  }

  // Financial year options (populated from GetProposedYear API)
  financialYears: string[] = [];
  /** Raw GetProposedYear rows, used to map a selected DisplayName to its ProposedYearId. */
  proposedYears: any[] = [];

  // Quarter options
  quarters: string[] = ['Q1', 'Q2', 'Q3', 'Q4'];

  // Month options
  months: string[] = [
    'January', 'February', 'March', 'April',
    'May', 'June', 'July', 'August',
    'September', 'October', 'November', 'December'
  ];
  reviewType: { id: number; name: string }[] = [
    { id: 1, name: 'Standard Review' },
    { id: 2, name: 'Go Live Review' },
    { id: 3, name: 'On Demand Review' },
    { id: 4, name: 'Special Project Review' },
  ];

  // Filter state — financialYear/quarter/month default to today's fiscal context (see applyDefaultFiscalSelection).
  filters: DashboardFilters = {
    financialYear: '',
    quarter: '',
    month: '',
    teamMembers: 'All Teams',
    client: '',
    region: 'All Regions',
    country: '',
    location: '',
    reviewType: 'All Reviews',
  };

  reviewTypes = [
    { count: 36, label: 'Standard Review' },
    { count: 16, label: 'Go-Live Review' },
    { count: 8,  label: 'On Demand Review' },
    { count: 5,  label: 'Special Project Review' },
  ];

  reviewStatuses = [
    { count: 55, label: 'Planning' },
    { count: 47, label: 'Scheduling' },
    { count: 32, label: 'Review Cycle' },
    { count: 4,  label: 'Report Draft' },
    { count: 20, label: 'Report Published' },
    { count: 28, label: 'Fieldwork in Progress' },
  ];

  reportRatings = [
    { count: 6,  label: 'Red Report Reviews',    color: '#e53935', darkText: false },
    { count: 4,  label: 'Yellow Report Reviews', color: '#f9a825', darkText: true  },
    { count: 5,  label: 'Blue Report Reviews',   color: '#8c9ecf', darkText: true  },
    { count: 36, label: 'Green Report Reviews',  color: '#2e7d5e', darkText: false },
  ];

  issueRatings = [
    { count: 25,  label: 'Red Issues',    color: '#e53935', darkText: false },
    { count: 30,  label: 'Yellow Issues', color: '#f9a825', darkText: true  },
    { count: 52,  label: 'Blue Issues',   color: '#8c9ecf', darkText: true  },
    { count: 250, label: 'Green Issues',  color: '#2e7d5e', darkText: false },
  ];

  upcomingMilestones = [
    { planDate: '10/18/2025', activity: 'Publishing Report', assigned: 'Som',  reviewId: 'R-7935' },
    { planDate: '10/14/2025', activity: 'Kick Off Meeting',  assigned: 'Jane', reviewId: 'R-9692' },
    { planDate: '10/10/2025', activity: 'Kick Off Meeting',  assigned: 'Alex', reviewId: 'R-2737' },
  ];

  overdueItems = [
    { reviewId: 'R-7553', reviewType: 'OO',  planDate: '10/9/2025', activity: 'Review Sign-off', assigned: 'Jane',  overdueDays: 7 },
    { reviewId: 'R-5309', reviewType: 'GLV', planDate: '10/9/2025', activity: 'Review Sign-off', assigned: 'Priya', overdueDays: 8 },
    { reviewId: 'R-5309', reviewType: 'GLV', planDate: '10/9/2025', activity: 'Review Sign-off', assigned: 'Priya', overdueDays: 8 },
    { reviewId: 'R-5309', reviewType: 'GLV', planDate: '10/9/2025', activity: 'Review Sign-off', assigned: 'Priya', overdueDays: 8 },
    { reviewId: 'R-5309', reviewType: 'GLV', planDate: '10/9/2025', activity: 'Review Sign-off', assigned: 'Priya', overdueDays: 8 },
    { reviewId: 'R-5309', reviewType: 'GLV', planDate: '10/9/2025', activity: 'Review Sign-off', assigned: 'Priya', overdueDays: 8 },
    { reviewId: 'R-5309', reviewType: 'GLV', planDate: '10/9/2025', activity: 'Review Sign-off', assigned: 'Priya', overdueDays: 8 },
    { reviewId: 'R-5309', reviewType: 'GLV', planDate: '10/9/2025', activity: 'Review Sign-off', assigned: 'Priya', overdueDays: 8 },
    { reviewId: 'R-5309', reviewType: 'GLV', planDate: '10/9/2025', activity: 'Review Sign-off', assigned: 'Priya', overdueDays: 8 },
    { reviewId: 'R-5309', reviewType: 'GLV', planDate: '10/9/2025', activity: 'Review Sign-off', assigned: 'Priya', overdueDays: 8 },
  ];

  issueCategories = [
    {
      title: 'Broad Issue Category (L1)',
      items: [
        { count: 16, label: 'Financial Reporting'  },
        { count: 15, label: 'Compliance Risk'       },
        { count: 9,  label: 'Operational Efficiency'},
      ]
    },
    {
      title: 'Detailed Issue Category (L2)',
      items: [
        { count: 6, label: 'IT General Controls'  },
        { count: 5, label: 'Data Access Control'  },
        { count: 4, label: 'Vendor Management'    },
      ]
    },
    {
      title: 'Risk Statement (L3)',
      items: [
        { count: 10, label: 'Errors'                           },
        { count: 7,  label: 'Fraud while processing transition'},
        { count: 3,  label: 'Category L3 -3'                  },
      ]
    },
  ];

  resetFilters(): void {
    this.filters = {
      financialYear: '',
      quarter: '',
      month: '',
      teamMembers: 'All Teams',
      client: '',
      region: 'All Regions',
      country: '',
      location: '',
      reviewType:'All Reviews'
    };
    this.isAllTeamsSelected = true;
    this.allTeamsChecked = true;
    this.selectedTeamMembers = [];
    this.teamMembersSearchTerm = '';
    this.teamMembersDropdownOpen = false;
    this.clientSearchTerm = '';
    this.clientDropdownOpen = false;
    this.closeGeoDropdown();
    this.financialYearDropdownOpen = false;
    this.quarterDropdownOpen = false;
    this.monthDropdownOpen = false;
    this.reviewTypeDropdownOpen = false;
    this.applyDefaultFiscalSelection();
    this.loadReviewDashboardCounts();
  }
}
